import React, { useState } from 'react';
import BentoCard from '../layout/BentoCard';
import TechBadge from '../ui/TechBadge';
import ProjectImage from '../ui/ProjectImage';
import { getFeaturedProjects, getNonFeaturedProjects } from '../../data/projects';

/**
 * ProjectsSection - Featured projects display
 * Shows main project large, others in grid
 */
const ProjectsSection = () => {
  const [activeProject, setActiveProject] = useState(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const featuredProjects = getFeaturedProjects();
  const nonFeaturedProjects = getNonFeaturedProjects();

  // First project is the hero/featured one
  const mainProject = featuredProjects[0];
  const secondaryProjects = [...featuredProjects.slice(1), ...nonFeaturedProjects];

  // Show only first 2 projects initially
  const visibleProjects = showAllProjects ? secondaryProjects : secondaryProjects.slice(0, 3);

  // Toggle description expansion
  const toggleDescription = (projectId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  return (
    <React.Fragment>
      {/* Featured Project - Large Card */}
      <BentoCard span="2" delay={200}>
        <div
          className="p-6 h-full relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, var(--color-bg-card) 0%, rgba(0, 212, 170, 0.05) 100%)`
          }}
        >
          <div
            className="absolute top-0 right-0 w-64 h-64 opacity-10 blur-2xl"
            style={{ background: 'radial-gradient(circle, var(--color-accent) 0%, transparent 70%)' }}
          />

          <div className="relative">
            {/* Project Image */}
            <ProjectImage
              type={mainProject.type}
              title={mainProject.title}
              className="mb-4"
            />

            <div className="flex items-center gap-2 mb-2">
              <span
                className="px-2 py-1 text-xs font-medium rounded-full"
                style={{
                  background: 'rgba(0, 212, 170, 0.2)',
                  color: 'var(--color-accent)'
                }}
              >
                Featured
              </span>
              <span
                className="text-xs"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {mainProject.category}
              </span>
            </div>

            <h3 className="text-xl font-bold mb-2">{mainProject.title}</h3>
            <div
              className="text-sm mb-4"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <p className={expandedDescriptions[mainProject.id] ? '' : 'line-clamp-2'}>
                {expandedDescriptions[mainProject.id]
                  ? mainProject.longDescription || mainProject.description
                  : mainProject.description}
              </p>
              {mainProject.longDescription && (
                <button
                  onClick={() => toggleDescription(mainProject.id)}
                  className="text-xs mt-1 hover:text-[var(--color-accent)] transition-colors"
                  style={{ color: 'var(--color-accent-alt)' }}
                >
                  {expandedDescriptions[mainProject.id] ? '↑ Show less' : '→ Read more'}
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {mainProject.tech.slice(0, 5).map((tech, i) => (
                <TechBadge key={i} tech={tech} />
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span
                className="text-sm font-mono"
                style={{ color: 'var(--color-accent)' }}
              >
                {mainProject.metrics}
              </span>
              <a
                href='https://smartescrow.eu/'
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium transition-colors duration-300 hover:text-[var(--color-accent)]"
                style={{ color: 'var(--color-text-muted)' }}
              >
                View Case Study →
              </a>
            </div>
          </div>
        </div>
      </BentoCard>

      {/* Secondary Projects */}
      {visibleProjects.map((project, index) => (
        <BentoCard key={project.id} span="1" delay={300 + (index * 50)}>
          <div
            className="p-6 h-full relative overflow-hidden cursor-pointer group"
            onClick={() => setActiveProject(activeProject === project.id ? null : project.id)}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: `linear-gradient(135deg, transparent 0%, ${project.gradient.split(' ')[1]?.replace('/20', '/10') || 'rgba(0, 212, 170, 0.1)-'} 100%)`
              }}
            />

            <div className="relative">
              {/* Project Image */}
              <ProjectImage
                type={project.type}
                title={project.title}
                className="mb-3"
              />

              <span
                className="text-xs uppercase tracking-wider font-medium"
                style={{ color: 'var(--color-accent)' }}
              >
                {project.category}
              </span>

              <h3 className="text-lg font-bold mt-2 mb-2 group-hover:text-[var(--color-accent)] transition-colors duration-300">
                {project.title}
              </h3>

              <div
                className="text-sm mb-4"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <p className={expandedDescriptions[project.id] ? '' : 'line-clamp-2'}>
                  {expandedDescriptions[project.id]
                    ? project.longDescription || project.description
                    : project.description}
                </p>
                {project.longDescription && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDescription(project.id);
                    }}
                    className="text-xs mt-1 hover:text-[var(--color-accent)] transition-colors"
                    style={{ color: 'var(--color-accent-alt)' }}
                  >
                    {expandedDescriptions[project.id] ? '↑ Show less' : '→ Read more'}
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {project.tech.slice(0, 3).map((tech, j) => (
                  <span
                    key={j}
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      color: 'var(--color-text-muted)'
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div
                className="text-xs font-mono"
                style={{ color: 'var(--color-accent)' }}
              >
                {project.metrics}
              </div>
            </div>
          </div>
        </BentoCard>
      ))}

      {/* Show More Projects Button */}
      {secondaryProjects.length > 2 && (
        <BentoCard
          span="full"
          delay={300 + (2 * 50)}
          hover={false}
        >
          <div className="p-6 text-center">
            <button
              onClick={() => setShowAllProjects(!showAllProjects)}
              className="w-full py-4 px-6 rounded-xl font-medium border-2 transition-all duration-300 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)]/10"
              style={{
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)'
              }}
            >
              {showAllProjects ? (
                <>
                  <span className="mr-2">↑</span>
                  Show Less Projects
                </>
              ) : (
                <>
                  <span className="mr-2">→</span>
                  View {secondaryProjects.length - 2} More Projects
                </>
              )}
            </button>
          </div>
        </BentoCard>
      )}
    </React.Fragment>
  );
};

export default ProjectsSection;
